import React, { useEffect, useState } from 'react';

const toIsoDate = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const addDaysIso = (dateText, days) => {
  if (!dateText) return '';
  const [y, m, d] = String(dateText).split('-').map(Number);
  if (!Number.isInteger(y)) return '';
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + Number(days || 0));
  return toIsoDate(dt);
};

const GROUP_TYPES = ['CORPORATE', 'TOUR', 'WEDDING', 'CONFERENCE', 'OTHER'];
const GROUP_STATUSES = ['TENTATIVE', 'CONFIRMED', 'CHECKED_IN', 'COMPLETED', 'CANCELLED'];

const emptyLine = { room_type_id: '', room_id: '', adults: 1, children: 0, rate_plan_code: 'RACK', rate_amount: 0 };

function PmsGroupBooking({ token, selectedProperty }) {
  const [groups, setGroups] = useState([]);
  const [view, setView] = useState('list'); // list | form | detail
  const [editingGroup, setEditingGroup] = useState(null);
  const [form, setForm] = useState(buildEmptyForm());
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [types, setTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  // Detail view
  const [detailGroup, setDetailGroup] = useState(null);
  const [roomingList, setRoomingList] = useState([]);

  // Quick Add
  const [quickAdd, setQuickAdd] = useState({ room_type_id: '', count: 1, adults: 2, children: 0, rate_plan_code: 'RACK' });
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [paxTarget, setPaxTarget] = useState({ adults: 0, children: 0 });

  function buildEmptyForm() {
    const arrival = toIsoDate(new Date());
    const departure = addDaysIso(arrival, 1);
    return {
      group_name: '', group_type: 'CORPORATE', contact_person: '', contact_mobile: '', contact_email: '',
      arrival_date: arrival, departure_date: departure, notes: '', status: 'TENTATIVE',
      // Reservation fields for linked booking
      primary_guest_name: '', mobile_number: '', email: '', booking_source: 'DIRECT'
    };
  }

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadGroups = async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/pms/groups', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load groups');
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load groups');
    }
  };

  const loadMasters = async () => {
    if (!selectedProperty?.id) return;
    try {
      const [typeRes, roomRes, planRes] = await Promise.all([
        fetch('/api/v1/pms/room-types', { headers: headers() }),
        fetch('/api/v1/pms/rooms', { headers: headers() }),
        fetch('/api/v1/pms/rate-plans?all=1', { headers: headers() })
      ]);
      const tData = await typeRes.json();
      const rData = await roomRes.json();
      const pData = await planRes.json();
      setTypes(Array.isArray(tData) ? tData : []);
      setRooms(Array.isArray(rData) ? rData : []);
      setRatePlans(Array.isArray(pData) ? pData.filter((p) => p.is_active !== false) : []);
    } catch (_) {}
  };

  useEffect(() => {
    loadGroups();
    loadMasters();
  }, [selectedProperty?.id]);

  // Quick Add Rooms — total pax distribution
  const addQuickRooms = () => {
    const { room_type_id, count, adults, children, rate_plan_code } = quickAdd;
    if (!room_type_id || count < 1) return;
    const qty = Math.min(Math.max(1, Number(count)), 50);
    const totalAdults = Math.max(0, Number(adults || 0));
    const totalChildren = Math.max(0, Number(children || 0));

    const selectedType = types.find((rt) => String(rt.room_type_id) === String(room_type_id));
    const maxA = selectedType ? Math.max(1, Number(selectedType.max_adults || 2)) : 99;
    const maxC = selectedType ? Math.max(0, Number(selectedType.max_children || 2)) : 99;
    const maxPax = selectedType ? Math.max(1, Number(selectedType.max_pax || 3)) : 99;

    const baseAdults = qty > 0 ? Math.floor(totalAdults / qty) : 1;
    let remainderAdults = qty > 0 ? totalAdults - (baseAdults * qty) : 0;
    const baseChildren = qty > 0 ? Math.floor(totalChildren / qty) : 0;
    let remainderChildren = qty > 0 ? totalChildren - (baseChildren * qty) : 0;

    const newLines = Array.from({ length: qty }, (_, i) => {
      let a = baseAdults + (remainderAdults > 0 ? 1 : 0);
      if (remainderAdults > 0) remainderAdults--;
      let c = baseChildren + (remainderChildren > 0 ? 1 : 0);
      if (remainderChildren > 0) remainderChildren--;
      a = Math.min(Math.max(1, a), maxA);
      c = Math.min(Math.max(0, c), maxC);
      if (a + c > maxPax) c = Math.max(0, maxPax - a);
      return {
        ...emptyLine,
        room_type_id: String(room_type_id),
        adults: a,
        children: c,
        rate_plan_code: rate_plan_code || 'RACK'
      };
    });

    setPaxTarget((prev) => ({
      adults: prev.adults + totalAdults,
      children: prev.children + totalChildren
    }));

    setLines((prev) => {
      const existingNonEmpty = prev.filter((l) => l.room_type_id);
      return existingNonEmpty.length ? [...prev, ...newLines] : newLines;
    });
    setQuickAddOpen(false);
    setQuickAdd({ room_type_id: '', count: 1, adults: 2, children: 0, rate_plan_code: 'RACK' });
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const updated = { ...l, ...patch };
      if (patch.room_id) {
        const picked = rooms.find((r) => String(r.room_id) === String(patch.room_id));
        if (picked?.room_type_id) updated.room_type_id = String(picked.room_type_id);
      }
      if (patch.room_type_id && String(patch.room_type_id) !== String(l.room_type_id)) {
        updated.room_id = '';
        updated.rate_plan_code = 'RACK';
      }
      return updated;
    }));
  };

  const removeLine = (idx) => {
    setLines((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [{ ...emptyLine }];
    });
  };

  const openNewForm = () => {
    setForm(buildEmptyForm());
    setLines([{ ...emptyLine }]);
    setEditingGroup(null);
    setView('form');
    setError('');
    setMessage('');
  };

  const openDetail = async (group) => {
    setError('');
    setMessage('');
    try {
      const [groupRes, rlRes] = await Promise.all([
        fetch(`/api/v1/pms/groups/${group.group_id}`, { headers: headers() }),
        fetch(`/api/v1/pms/groups/${group.group_id}/rooming-list`, { headers: headers() })
      ]);
      const gData = await groupRes.json();
      const rlData = await rlRes.json();
      if (!groupRes.ok) throw new Error(gData?.error || 'Failed to load group');
      setDetailGroup(gData);
      setRoomingList(Array.isArray(rlData) ? rlData : []);
      setView('detail');
    } catch (err) {
      setError(err?.message || 'Failed to load group detail');
    }
  };

  const openEditForm = (group) => {
    setForm({
      group_name: group.group_name || '',
      group_type: group.group_type || 'CORPORATE',
      contact_person: group.contact_person || '',
      contact_mobile: group.contact_mobile || '',
      contact_email: group.contact_email || '',
      arrival_date: group.arrival_date || '',
      departure_date: group.departure_date || '',
      notes: group.notes || '',
      status: group.status || 'TENTATIVE',
      primary_guest_name: '', mobile_number: '', email: '', booking_source: 'DIRECT'
    });
    setLines([{ ...emptyLine }]);
    setEditingGroup(group);
    setView('form');
    setError('');
    setMessage('');
  };

  const submitGroup = async () => {
    if (!form.group_name.trim()) return setError('Group name is required');
    if (!form.arrival_date || !form.departure_date) return setError('Arrival and departure dates are required');
    if (form.arrival_date < toIsoDate(new Date())) return setError('Arrival date cannot be in the past');
    if (form.departure_date <= form.arrival_date) return setError('Departure date must be after arrival date');
    // Bug 8675 — validate Contact Mobile / Email format when provided.
    const contactMobile = String(form.contact_mobile || '').trim();
    if (contactMobile && !/^\d{10}$/.test(contactMobile.replace(/\D/g, ''))) {
      return setError('Contact Mobile must be a valid 10-digit number');
    }
    const contactEmail = String(form.contact_email || '').trim();
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      return setError('Contact Email is not a valid email address');
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const isEdit = !!editingGroup;
      const totalRooms = lines.filter((l) => l.room_type_id).length;
      const groupPayload = {
        group_name: form.group_name,
        group_type: form.group_type,
        contact_person: form.contact_person,
        contact_mobile: form.contact_mobile,
        contact_email: form.contact_email,
        arrival_date: form.arrival_date,
        departure_date: form.departure_date,
        total_rooms: totalRooms,
        notes: form.notes,
        status: form.status
      };

      let groupId;
      if (isEdit) {
        const res = await fetch(`/api/v1/pms/groups/${editingGroup.group_id}`, {
          method: 'PUT', headers: headers(), body: JSON.stringify(groupPayload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to update group');
        groupId = editingGroup.group_id;
      } else {
        const res = await fetch('/api/v1/pms/groups', {
          method: 'POST', headers: headers(), body: JSON.stringify(groupPayload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to create group');
        groupId = data.group_id;
      }

      // Create linked reservation if there are room lines with types selected
      const validLines = lines.filter((l) => l.room_type_id);
      if (validLines.length > 0 && !isEdit) {
        const resPayload = {
          group_id: groupId,
          arrival_date: form.arrival_date,
          departure_date: form.departure_date,
          primary_guest_name: form.contact_person || form.group_name,
          mobile_number: form.contact_mobile || '',
          email: form.contact_email || '',
          booking_source: form.booking_source || 'DIRECT',
          status: form.status === 'CONFIRMED' ? 'CONFIRMED' : 'TENTATIVE',
          room_count: validLines.length,
          total_pax: validLines.reduce((s, l) => s + Number(l.adults || 0) + Number(l.children || 0), 0),
          lines: validLines.map((l) => ({
            room_type_id: Number(l.room_type_id),
            room_id: l.room_id ? Number(l.room_id) : null,
            arrival_date: form.arrival_date,
            departure_date: form.departure_date,
            adults: Number(l.adults || 1),
            children: Number(l.children || 0),
            rate_plan_code: l.rate_plan_code || 'RACK',
            rate_amount: Number(l.rate_amount || 0)
          }))
        };
        const rRes = await fetch('/api/v1/pms/reservations', {
          method: 'POST', headers: headers(), body: JSON.stringify(resPayload)
        });
        const rData = await rRes.json();
        if (!rRes.ok) throw new Error(rData?.error || 'Failed to create linked reservation');
        setMessage(`Group "${form.group_name}" created with ${validLines.length} rooms. Reservation: ${rData.reservation_no}`);
      } else {
        setMessage(isEdit ? `Group "${form.group_name}" updated.` : `Group "${form.group_name}" created.`);
      }

      setView('list');
      loadGroups();
    } catch (err) {
      setError(err?.message || 'Failed to save group');
    } finally {
      setBusy(false);
    }
  };

  const statusColor = (s) => {
    const map = { TENTATIVE: '#eab308', CONFIRMED: '#2563eb', CHECKED_IN: 'var(--theme-success)', COMPLETED: '#6b7280', CANCELLED: 'var(--theme-danger)' };
    return map[s] || '#6b7280';
  };

  if (!selectedProperty?.id) {
    return <div style={{ padding: '1rem', color: '#6b7280' }}>Please select a property to manage Group Bookings.</div>;
  }

  /* ======== DETAIL VIEW ======== */
  if (view === 'detail' && detailGroup) {
    return (
      <div style={{ padding: '0.6rem 1rem', maxWidth: 1100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
          <button onClick={() => { setView('list'); setDetailGroup(null); }}
            style={{ background: 'var(--theme-border-light)', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
            &larr; Back
          </button>
          <h3 style={{ margin: 0 }}>Group: {detailGroup.group_name}</h3>
          <span style={{ background: statusColor(detailGroup.status), color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
            {detailGroup.status}
          </span>
          <button onClick={() => openEditForm(detailGroup)}
            style={{ marginLeft: 'auto', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontWeight: 600 }}>
            Edit Group
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem', fontSize: 13, marginBottom: '0.8rem',
          background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '0.6rem 0.8rem' }}>
          <div><strong>Type:</strong> {detailGroup.group_type}</div>
          <div><strong>Contact:</strong> {detailGroup.contact_person || '-'}</div>
          <div><strong>Mobile:</strong> {detailGroup.contact_mobile || '-'}</div>
          <div><strong>Email:</strong> {detailGroup.contact_email || '-'}</div>
          <div><strong>Arrival:</strong> {detailGroup.arrival_date}</div>
          <div><strong>Departure:</strong> {detailGroup.departure_date}</div>
          <div><strong>Total Rooms:</strong> {detailGroup.total_rooms}</div>
        </div>

        {detailGroup.notes && (
          <div style={{ fontSize: 13, color: '#475569', marginBottom: '0.6rem' }}>
            <strong>Notes:</strong> {detailGroup.notes}
          </div>
        )}

        {/* Linked Reservations */}
        {detailGroup.reservations?.length > 0 && (
          <div style={{ marginBottom: '0.8rem' }}>
            <h4 style={{ margin: '0 0 0.4rem' }}>Linked Reservations</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#2b6cb0', color: '#fff' }}>
                  <th style={{ padding: '0.35rem' }}>Res #</th>
                  <th style={{ padding: '0.35rem' }}>Guest</th>
                  <th style={{ padding: '0.35rem' }}>Status</th>
                  <th style={{ padding: '0.35rem' }}>Rooms</th>
                  <th style={{ padding: '0.35rem' }}>Arrival</th>
                  <th style={{ padding: '0.35rem' }}>Departure</th>
                </tr>
              </thead>
              <tbody>
                {detailGroup.reservations.map((r) => (
                  <tr key={r.reservation_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                    <td style={{ padding: '0.35rem', fontWeight: 600 }}>{r.reservation_no}</td>
                    <td style={{ padding: '0.35rem' }}>{r.primary_guest_name}</td>
                    <td style={{ padding: '0.35rem' }}>
                      <span style={{ background: statusColor(r.status), color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>{r.status}</span>
                    </td>
                    <td style={{ padding: '0.35rem' }}>{r.room_count}</td>
                    <td style={{ padding: '0.35rem' }}>{r.arrival_date}</td>
                    <td style={{ padding: '0.35rem' }}>{r.departure_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Rooming List */}
        <h4 style={{ margin: '0 0 0.4rem' }}>Rooming List ({roomingList.length} rooms)</h4>
        {roomingList.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#1e40af', color: '#fff' }}>
                  <th style={{ padding: '0.35rem' }}>Res #</th>
                  <th style={{ padding: '0.35rem' }}>Room Type</th>
                  <th style={{ padding: '0.35rem' }}>Room</th>
                  <th style={{ padding: '0.35rem' }}>Adults</th>
                  <th style={{ padding: '0.35rem' }}>Children</th>
                  <th style={{ padding: '0.35rem' }}>Rate Plan</th>
                  <th style={{ padding: '0.35rem', textAlign: 'right' }}>Rate</th>
                  <th style={{ padding: '0.35rem' }}>Guest</th>
                  <th style={{ padding: '0.35rem' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {roomingList.map((rl, idx) => (
                  <tr key={`rl-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                    <td style={{ padding: '0.35rem' }}>{rl.reservation_no}</td>
                    <td style={{ padding: '0.35rem' }}>{rl.room_type_name || '-'}</td>
                    <td style={{ padding: '0.35rem', fontWeight: 600 }}>{rl.room_number || 'Unassigned'}</td>
                    <td style={{ padding: '0.35rem' }}>{rl.adults}</td>
                    <td style={{ padding: '0.35rem' }}>{rl.children}</td>
                    <td style={{ padding: '0.35rem' }}>{rl.rate_plan_code}</td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>{Number(rl.rate_amount || 0).toFixed(2)}</td>
                    <td style={{ padding: '0.35rem' }}>{rl.primary_guest_name || '-'}</td>
                    <td style={{ padding: '0.35rem' }}>
                      <span style={{ background: statusColor(rl.status), color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>{rl.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: '#9ca3af', fontSize: 13, padding: '0.5rem 0' }}>No room lines found for this group.</div>
        )}
      </div>
    );
  }

  /* ======== FORM VIEW ======== */
  if (view === 'form') {
    return (
      <div style={{ padding: '0.6rem 1rem', maxWidth: 900 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
          <button onClick={() => { setView('list'); setEditingGroup(null); }}
            style={{ background: 'var(--theme-border-light)', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
            &larr; Back
          </button>
          <h3 style={{ margin: 0 }}>{editingGroup ? 'Edit Group' : 'New Group Booking'}</h3>
        </div>

        {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '0.4rem 0.6rem', color: '#991b1b', fontSize: 13, marginBottom: '0.5rem' }}>{error}</div>}
        {message && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '0.4rem 0.6rem', color: '#166534', fontSize: 13, marginBottom: '0.5rem' }}>{message}</div>}

        {/* Group Header */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem', marginBottom: '0.7rem',
          background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, padding: '0.6rem 0.8rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
            Group Name *
            <input value={form.group_name} onChange={(e) => setForm({ ...form, group_name: e.target.value })}
              placeholder="e.g. Acme Corp Annual Meet" style={{ marginTop: 2 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
            Group Type
            <select value={form.group_type} onChange={(e) => setForm({ ...form, group_type: e.target.value })} style={{ marginTop: 2 }}>
              {GROUP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
            Contact Person
            <input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} style={{ marginTop: 2 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
            Contact Mobile
            <input type="tel" inputMode="numeric" maxLength={10} value={form.contact_mobile}
              onChange={(e) => setForm({ ...form, contact_mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              placeholder="10-digit mobile" style={{ marginTop: 2 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
            Contact Email
            <input type="email" value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="name@example.com" style={{ marginTop: 2 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
            Arrival Date *
            <input type="date" value={form.arrival_date}
              min={toIsoDate(new Date())}
              onChange={(e) => {
                const arrival = e.target.value;
                const departure = form.departure_date && form.departure_date > arrival ? form.departure_date : addDaysIso(arrival, 1);
                setForm({ ...form, arrival_date: arrival, departure_date: departure });
              }}
              style={{ marginTop: 2 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
            Departure Date *
            <input type="date" value={form.departure_date}
              min={form.arrival_date ? addDaysIso(form.arrival_date, 1) : toIsoDate(new Date())}
              onChange={(e) => setForm({ ...form, departure_date: e.target.value })}
              style={{ marginTop: 2 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
            Status
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={{ marginTop: 2 }}>
              {GROUP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, marginBottom: '0.7rem' }}>
          Notes
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2} style={{ marginTop: 2, resize: 'vertical' }} />
        </label>

        {/* Room Allocation */}
        {!editingGroup && (
          <>
            <h4 style={{ margin: '0 0 0.4rem', color: '#1e40af' }}>Room Allocation</h4>

            {/* Quick Add */}
            <div style={{ marginBottom: '0.4rem' }}>
              <button type="button" onClick={() => setQuickAddOpen((v) => !v)}
                style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: 6,
                  padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {quickAddOpen ? '− Quick Add Rooms' : '+ Quick Add Rooms'}
              </button>
              {quickAddOpen && (
                <div style={{ border: '1px solid #c7d2fe', borderRadius: 8, padding: '0.6rem 0.8rem', marginTop: '0.3rem',
                  background: '#f5f7ff', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600 }}>
                    Room Type
                    <select value={quickAdd.room_type_id}
                      onChange={(e) => {
                        const rtId = e.target.value;
                        const rt = types.find((t) => String(t.room_type_id) === String(rtId));
                        setQuickAdd((p) => ({
                          ...p,
                          room_type_id: rtId,
                          rate_plan_code: 'RACK',
                          adults: Number(p.count || 1) * Math.min(2, rt ? Math.max(1, Number(rt.max_adults || 2)) : 2),
                          children: 0
                        }));
                      }}
                      style={{ marginTop: 2, minWidth: 130 }}>
                      <option value="">Select</option>
                      {types.map((t) => <option key={t.room_type_id} value={t.room_type_id}>{t.room_type_name}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600 }}>
                    No. of Rooms
                    <input type="number" min={1} max={50} value={quickAdd.count}
                      onChange={(e) => setQuickAdd((p) => ({ ...p, count: e.target.value }))}
                      style={{ marginTop: 2, width: 55 }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600 }}>
                    Total Adults
                    <input type="number" min={0} max={999} value={quickAdd.adults}
                      onChange={(e) => setQuickAdd((p) => ({ ...p, adults: e.target.value }))}
                      style={{ marginTop: 2, width: 60 }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600 }}>
                    Total Children
                    <input type="number" min={0} max={999} value={quickAdd.children}
                      onChange={(e) => setQuickAdd((p) => ({ ...p, children: e.target.value }))}
                      style={{ marginTop: 2, width: 60 }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600 }}>
                    Rate Plan
                    <select value={quickAdd.rate_plan_code}
                      onChange={(e) => setQuickAdd((p) => ({ ...p, rate_plan_code: e.target.value }))}
                      style={{ marginTop: 2, minWidth: 120 }}>
                      {ratePlans
                        .filter((rp) => !quickAdd.room_type_id || String(rp.room_type_id || '') === String(quickAdd.room_type_id))
                        .map((rp) => <option key={rp.rate_plan_id} value={rp.rate_plan_code}>{rp.rate_plan_code} - {rp.rate_plan_name}</option>)}
                      {!ratePlans.filter((rp) => !quickAdd.room_type_id || String(rp.room_type_id || '') === String(quickAdd.room_type_id)).length
                        ? <option value="RACK">RACK</option> : null}
                    </select>
                  </label>
                  <button type="button" disabled={!quickAdd.room_type_id || quickAdd.count < 1}
                    onClick={addQuickRooms}
                    style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6,
                      padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      opacity: (!quickAdd.room_type_id || quickAdd.count < 1) ? 0.5 : 1 }}>
                    Add {Math.min(Math.max(1, Number(quickAdd.count) || 1), 50)} Room{Number(quickAdd.count) !== 1 ? 's' : ''}
                  </button>
                </div>
              )}
            </div>

            {/* Lines Table */}
            <div style={{ overflowX: 'auto', marginBottom: '0.6rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#2b6cb0', color: '#fff' }}>
                    <th style={{ padding: '0.35rem' }}>#</th>
                    <th style={{ padding: '0.35rem' }}>Room Type</th>
                    <th style={{ padding: '0.35rem' }}>Room</th>
                    <th style={{ padding: '0.35rem' }}>Adults</th>
                    <th style={{ padding: '0.35rem' }}>Children</th>
                    <th style={{ padding: '0.35rem' }}>Rate Plan</th>
                    <th style={{ padding: '0.35rem' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => (
                    <tr key={`gl-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={{ padding: '0.35rem', color: '#6b7280', textAlign: 'center' }}>{idx + 1}</td>
                      <td style={{ padding: '0.35rem' }}>
                        <select value={line.room_type_id} onChange={(e) => updateLine(idx, { room_type_id: e.target.value })} style={{ minWidth: 120 }}>
                          <option value="">Select</option>
                          {types.map((t) => <option key={t.room_type_id} value={t.room_type_id}>{t.room_type_name}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '0.35rem' }}>
                        <select value={line.room_id || ''} onChange={(e) => updateLine(idx, { room_id: e.target.value })} style={{ minWidth: 100 }}>
                          <option value="">Unassigned</option>
                          {rooms
                            // Bug 8674 — don't offer occupied or maintenance-blocked
                            // rooms for a new group booking (keep the currently
                            // selected room visible so it never vanishes from its row).
                            .filter((r) => (!line.room_type_id || String(r.room_type_id) === String(line.room_type_id))
                              && r.is_active !== false
                              && (String(r.occupancy_status || '').toUpperCase() !== 'OCCUPIED' || String(r.room_id) === String(line.room_id))
                              && (!['BLOCKED', 'OUT_OF_ORDER', 'PAINTING'].includes(String(r.maintenance_status || '').toUpperCase()) || String(r.room_id) === String(line.room_id)))
                            .map((r) => <option key={r.room_id} value={r.room_id}>{r.room_number}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '0.35rem' }}>
                        <input type="number" min={1} max={10} value={line.adults}
                          onChange={(e) => updateLine(idx, { adults: Number(e.target.value || 1) })}
                          style={{ width: 50 }} />
                      </td>
                      <td style={{ padding: '0.35rem' }}>
                        <input type="number" min={0} max={10} value={line.children}
                          onChange={(e) => updateLine(idx, { children: Number(e.target.value || 0) })}
                          style={{ width: 50 }} />
                      </td>
                      <td style={{ padding: '0.35rem' }}>
                        <select value={line.rate_plan_code || 'RACK'} onChange={(e) => updateLine(idx, { rate_plan_code: e.target.value })} style={{ minWidth: 110 }}>
                          {ratePlans
                            .filter((rp) => !line.room_type_id || String(rp.room_type_id || '') === String(line.room_type_id))
                            .map((rp) => <option key={rp.rate_plan_id} value={rp.rate_plan_code}>{rp.rate_plan_code} - {rp.rate_plan_name}</option>)}
                          {!ratePlans.filter((rp) => !line.room_type_id || String(rp.room_type_id || '') === String(line.room_type_id)).length
                            ? <option value="RACK">RACK</option> : null}
                        </select>
                      </td>
                      <td style={{ padding: '0.35rem' }}>
                        <button onClick={() => removeLine(idx)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Remaining pax banner */}
            {paxTarget.adults > 0 && (() => {
              const assignedAdults = lines.reduce((s, l) => s + Number(l.adults || 0), 0);
              const assignedChildren = lines.reduce((s, l) => s + Number(l.children || 0), 0);
              const remA = paxTarget.adults - assignedAdults;
              const remC = paxTarget.children - assignedChildren;
              if (remA <= 0 && remC <= 0) return null;
              return (
                <div style={{ marginBottom: '0.5rem', padding: '0.45rem 0.7rem', background: '#fff7ed', border: '1px solid #fdba74',
                  borderRadius: 6, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                  {remA > 0 && <span>{remA} adult{remA !== 1 ? 's' : ''} unassigned (requested {paxTarget.adults}, placed {assignedAdults}). </span>}
                  {remC > 0 && <span>{remC} child{remC !== 1 ? 'ren' : ''} unassigned (requested {paxTarget.children}, placed {assignedChildren}). </span>}
                  <span>Add more rooms or adjust counts in existing rooms.</span>
                </div>
              );
            })()}

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem' }}>
              <button onClick={() => setLines((prev) => [...prev, { ...emptyLine }])}>Add Line</button>
              <span style={{ fontSize: 13, color: '#475569', alignSelf: 'center' }}>
                {lines.filter((l) => l.room_type_id).length} room{lines.filter((l) => l.room_type_id).length !== 1 ? 's' : ''} allocated
              </span>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <button onClick={submitGroup} disabled={busy}
            style={{ background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: 6,
              padding: '8px 22px', fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Saving...' : (editingGroup ? 'Update Group' : 'Create Group Booking')}
          </button>
          <button onClick={() => { setView('list'); setEditingGroup(null); setPaxTarget({ adults: 0, children: 0 }); }}>Cancel</button>
        </div>
      </div>
    );
  }

  /* ======== LIST VIEW ======== */
  return (
    <div style={{ padding: '0.6rem 1rem', maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
        <h3 style={{ margin: 0 }}>Group Bookings</h3>
        <button onClick={openNewForm}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
            padding: '7px 18px', fontWeight: 600, cursor: 'pointer' }}>
          + New Group
        </button>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '0.4rem 0.6rem', color: '#991b1b', fontSize: 13, marginBottom: '0.5rem' }}>{error}</div>}
      {message && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '0.4rem 0.6rem', color: '#166534', fontSize: 13, marginBottom: '0.5rem' }}>{message}</div>}

      {groups.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
          No group bookings found. Click "+ New Group" to create one.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#2b6cb0', color: '#fff' }}>
                <th style={{ padding: '0.4rem' }}>Group Name</th>
                <th style={{ padding: '0.4rem' }}>Type</th>
                <th style={{ padding: '0.4rem' }}>Contact</th>
                <th style={{ padding: '0.4rem' }}>Rooms</th>
                <th style={{ padding: '0.4rem' }}>Arrival</th>
                <th style={{ padding: '0.4rem' }}>Departure</th>
                <th style={{ padding: '0.4rem' }}>Reservations</th>
                <th style={{ padding: '0.4rem' }}>Status</th>
                <th style={{ padding: '0.4rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.group_id} style={{ borderBottom: '1px solid var(--theme-border)', cursor: 'pointer' }}
                  onClick={() => openDetail(g)}>
                  <td style={{ padding: '0.4rem', fontWeight: 600 }}>{g.group_name}</td>
                  <td style={{ padding: '0.4rem' }}>{g.group_type}</td>
                  <td style={{ padding: '0.4rem' }}>{g.contact_person || '-'}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'center' }}>{g.total_rooms}</td>
                  <td style={{ padding: '0.4rem' }}>{g.arrival_date}</td>
                  <td style={{ padding: '0.4rem' }}>{g.departure_date}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'center' }}>{g.reservation_count || 0}</td>
                  <td style={{ padding: '0.4rem' }}>
                    <span style={{ background: statusColor(g.status), color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                      {g.status}
                    </span>
                  </td>
                  <td style={{ padding: '0.4rem' }} onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEditForm(g)} style={{ fontSize: 12, marginRight: 4 }}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PmsGroupBooking;
